import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Content, Close, Title } from '@radix-ui/react-dialog';
import closeIcon from '@/assets/icons/close.svg';
import { QuickDonationButton } from '../PublicDonation/types';
import cdStyles from '@/components/ui/ConfirmationDialog/ConfirmationDialog.module.css';
import styles from './DonationsAdmin.module.css';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: QuickDonationButton | null;
  isNew?: boolean;
  currencySymbol: string;
  isPending: boolean;
  onSave: (button: QuickDonationButton) => void;
}

const EMPTY: QuickDonationButton = { amount: 0, label: '', currency: 'ILS', months: 1 };

const QuickButtonDialog = ({ open, onOpenChange, initial, isNew, currencySymbol, isPending, onSave }: Props) => {
  const { t } = useTranslation();
  const [btn, setBtn] = useState<QuickDonationButton>(EMPTY);

  useEffect(() => {
    if (open) setBtn(initial ?? { ...EMPTY });
  }, [open, initial]);

  const canSave = btn.amount > 0 && !isPending;
  const title = isNew ? t('donations.admin.newQuickButton') : t('donations.admin.editQuickButton');

  return (
    <Root open={open} onOpenChange={(o) => { if (!isPending) onOpenChange(o); }}>
      <Portal>
        <Overlay className={cdStyles.overlay} />
        <Content className={`${cdStyles.content} ${styles.qbDialog}`} aria-describedby={undefined}>
          <Close className={cdStyles.closeButton} disabled={isPending}>
            <img src={closeIcon} alt="" />
          </Close>

          <div className={styles.qbBody}>
            <Title className={styles.qbTitle}>{title}</Title>

            <div className={styles.qbForm}>
              <div className={styles.field}>
                <label>{t('donations.admin.amount')}</label>
                <div className={styles.amountInputGroup}>
                  <span className={styles.amountSymbol}>{currencySymbol}</span>
                  <input
                    type="number"
                    min={1}
                    placeholder={t('donations.admin.amountPlaceholder')}
                    value={btn.amount || ''}
                    onChange={(e) => setBtn((p) => ({ ...p, amount: Math.max(0, Number(e.target.value) || 0) }))}
                    disabled={isPending}
                  />
                </div>
              </div>
              <div className={styles.field}>
                <label>{t('donations.admin.months')}</label>
                <input
                  type="number"
                  min={1}
                  placeholder="1"
                  value={btn.months || 1}
                  onChange={(e) => setBtn((p) => ({ ...p, months: Math.max(1, Number(e.target.value) || 1) }))}
                  disabled={isPending}
                />
                <small className={styles.fieldHint}>{t('donations.admin.monthsHint')}</small>
              </div>
              <div className={styles.field}>
                <label>{t('donations.admin.label')}</label>
                <input
                  type="text"
                  placeholder={t('donations.admin.labelPlaceholder')}
                  value={btn.label || ''}
                  onChange={(e) => setBtn((p) => ({ ...p, label: e.target.value }))}
                  disabled={isPending}
                />
              </div>
            </div>

            <div className={styles.qbActions}>
              <button
                type="button"
                className={`${cdStyles.button} ${cdStyles.primaryButton}`}
                onClick={() => onSave(btn)}
                disabled={!canSave}
              >
                {isPending ? t('saving') : t('save')}
              </button>
              <button
                type="button"
                className={`${cdStyles.button} ${cdStyles.secondaryButton}`}
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </Content>
      </Portal>
    </Root>
  );
};

export default QuickButtonDialog;
